import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react';

import { Checkbox } from '../../../components/checkbox/checkbox';
import { Divider } from '../../../components/divider/divider';
import { Input } from '../../../components/input/input';
import { pageKey } from '../../../helpers/path';
import { useLang } from '../../../hooks/use-lang';
import { useQueryParams } from '../../../hooks/use-query-params';
import { IdValue, SortType } from '../../../types/props';
import { sortByName } from '../../table/helpers';
import { AlphabeticSortRadio } from '../../table/table-filter/table-alphabetic-sort/table-alphabetic-sort-radio';
import { DropdownFilterPopup } from '../dropdown-filter-popup/dropdown-filter-popup';
import s from './dropdown-filter-multi-select.module.css';

interface Props {
  name: string;
  isOpen: boolean;
  onOpen: (state: boolean) => void;
  options?: IdValue[];
}

export const DropdownFilterMultiSelect: FC<Props> = (props) => {
  const { name, isOpen, onOpen, options = [] } = props;
  const { addQueryParams, getQueryParams } = useQueryParams();
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<string[]>([]);
  const { t } = useLang();
  const [sortType, setSortType] = useState<SortType>('asc');

  useEffect(() => {
    const ids = getQueryParams()[name]?.split(',') || [];
    setChecked(ids);
  }, [name]);

  const handleSelect = (item: IdValue) => {
    const isChecked = checked?.some((value) => value === String(item.id));
    let result = checked;
    if (isChecked) {
      result = checked?.filter((value) => value !== String(item.id)) || [];
    } else {
      result = [...(checked || []), String(item.id)];
    }
    setChecked(result);
  };

  const onSearch = (value: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(value.currentTarget.value);

  const filteredOptions = useMemo(() => {
    return options.filter((item) => item.value.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => sortByName(a.value, b.value, sortType));
  }, [filteredOptions, sortType]);

  const handleOk = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent> | KeyboardEvent) => {
    addQueryParams({ [name]: checked.join(','), [pageKey]: '1' });
    onOpen(false);
    e.stopPropagation();
  };

  const handleReset = () => {
    addQueryParams({ [name]: undefined, [pageKey]: '1' });
    setChecked([]);
  };

  return (
    <DropdownFilterPopup
      isOpen={isOpen}
      onOpen={onOpen}
      onReset={handleReset}
      onSubmit={handleOk}
      name={name}
    >
      {options.length > 5 && (
        <>
          <Input value={search} onChange={onSearch} placeholder={t('Поиск')} />
          <AlphabeticSortRadio alphabetDirection={sortType} setAlphabetDirection={setSortType} />
          <Divider />
        </>
      )}
      <ul className={s.list}>
        {sortedOptions.map((item) => {
          const isChecked = checked.some((checkedItem) => checkedItem === String(item.id));
          return (
            <Checkbox
              checked={isChecked}
              onClick={() => handleSelect(item)}
              label={item.value}
              key={item.id}
            />
          );
        })}
      </ul>
    </DropdownFilterPopup>
  );
};
